import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { convertToBoolean } from "../../../_utils";
import {
  MdClose,
  MdInfo,
  MdCheckCircle,
  MdKeyboardArrowDown,
  MdWarning,
  MdReport,
} from "react-icons/md";
import styles from "./Toast.module.scss";

const typeOptions = {
  success: MdCheckCircle,
  warning: MdWarning,
  danger: MdReport,
  info: MdInfo,
};

const appearanceClassMap = {
  success: "toastAppearanceSuccess",
  warning: "toastAppearanceWarning",
  danger: "toastAppearanceDanger",
  info: "toastAppearanceInfo",
  link: "toastAppearanceLink",
  default: "toastAppearanceDefault",
};

const Toast = ({
  title,
  message,
  description,
  appearance,
  type,
  icon,
  actions,
  isExpanded = false,
  autoDismiss,
  ...props
}) => {
  const resolvedTitle = title ?? message;
  const resolvedAppearance = appearance ?? type;
  const [displayContent, setDisplayContent] = useState(isExpanded);
  const toastRef = useRef(null);
  const timerRef = useRef(null);
  const hasCloseAction = props?.onDismissed || props?.onClose;

  const handleClose = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (props?.onDismissed) props.onDismissed();
    if (props?.onClose) props.onClose();
  }, [props?.onDismissed, props?.onClose]);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      handleClose();
    }, 8000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [handleClose]);

  // Pause timer on hover, resume on leave
  const handleMouseEnter = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    timerRef.current = setTimeout(() => {
      handleClose();
    }, 8000);
  }, [handleClose]);

  const IconComponent =
    icon || typeOptions[resolvedAppearance] || typeOptions.info;

  return (
    <div
      className={`${styles["toast-container__toast"]} ${
        styles[
          appearanceClassMap[resolvedAppearance] || appearanceClassMap.default
        ]
      }`}
      ref={toastRef}
      toast-expanded={displayContent?.toString()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-start">
          <span className={styles["toast-container__toast--icon-title"]}>
            {React.createElement(IconComponent, {
              className: resolvedAppearance === "info" ? styles["info-icon"] : "",
            })}
          </span>
          <span className={styles["toast-container__toast--title"]}>
            {resolvedTitle}
          </span>
        </div>

        <div className="d-flex align-items-center gap-1">
          {!isExpanded && (
            <div
              className={`${styles["toast-container__toast--expand"]}`}
              onClick={() => setDisplayContent(!displayContent)}
            >
              <MdKeyboardArrowDown
                className={
                  displayContent ? styles["toast-container__toast--rotate"] : ""
                }
              />
            </div>
          )}
          {hasCloseAction && (
            <div
              className={`${styles["toast-container__toast--close"]}`}
              onClick={(event) => {
                event?.stopPropagation();
                handleClose();
              }}
            >
              <MdClose />
            </div>
          )}
        </div>
      </div>

      {(description || actions) && (
        <div
          className={`${styles["toast-content"]} ${
            displayContent || props.onDismissed || isExpanded
              ? styles["toast-content--visible"]
              : ""
          }`}
        >
          {description && (
            <div className={styles["toast-content__description"]}>
              {description}
            </div>
          )}
          <div
            className={styles["toast-content__buttons"]}
            style={{
              marginLeft: ["default", "link"].includes(resolvedAppearance)
                ? "20px"
                : "33px",
            }}
          >
            {actions?.map((action, index) => (
              <div
                key={index}
                className={`${styles["toast-content__buttons--button"]} ${
                  styles[
                    `toast-content__buttons--${resolvedAppearance || "default"}`
                  ]
                }`}
              >
                <div onClick={action?.onClick}>{action.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ToastContainer = ({
  autoDismiss,
  onDismissed,
  children,
  isExpanded = false,
}) => {
  const toastContainerRef = useRef(null);

  const [totalChildren, setTotalChildren] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [timerId, setTimerId] = useState(null);

  useEffect(() => {
    setToasts(children);
    setTotalChildren(children?.length);
  }, [children]);

  useEffect(() => {
    const clearTimer = () => clearTimeout(timerId);
    if (autoDismiss !== undefined && toasts.length > 0) {
      clearTimer();
      setTimerId(setTimeout(removeFirstToast, 8000));
    }
  }, [autoDismiss, toasts]);

  const removeFirstToast = () => onDismissed();

  const container = (
    <div
      className={styles["toast-container"]}
      ref={toastContainerRef}
      style={{
        ...(totalChildren === 0 && { display: "none" }),
      }}
    >
      {React.Children.toArray(toasts)
        ?.slice()
        ?.reverse()
        ?.map((toast, index) => {
          const key = toast.key || index;
          const childProps = onDismissed ? { onDismissed } : {};

          return (
            <Children
              key={key}
              index={index}
              timerId={timerId}
              setTimerId={setTimerId}
              removeFirstToast={removeFirstToast}
              {...childProps}
              autoDismiss={autoDismiss}
              isExpanded={isExpanded}
            >
              {toast}
            </Children>
          );
        })}
    </div>
  );

  if (typeof document === "undefined") {
    return container;
  }

  return createPortal(container, document.body);
};

const Children = forwardRef(
  (
    {
      children,
      timerId,
      removeFirstToast,
      setTimerId,
      onDismissed,
      autoDismiss,
      index,
    },
    ref
  ) => {
    const handleMouseEnter = () => {
      if (autoDismiss !== undefined) clearTimeout(timerId);
    };

    const handleMouseLeave = () => {
      if (autoDismiss !== undefined)
        setTimerId(setTimeout(removeFirstToast, 8000));
    };

    return (
      <div
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={styles["children-container"]}
      >
        {React.cloneElement(children, {
          onDismissed,
          autoDismiss,
          firsToast: index === 0,
        })}
      </div>
    );
  }
);

export { Toast, ToastContainer };
